import { useMemo, useState } from 'react'
import { Download, Eye, FileText, Info, Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { FileKindBadge } from '@/features/resident/utils/fileKind'
import { showApiError } from '@/lib/utils/notifications'
import { openServeFileInNewTab, triggerServeFileDownload } from '../utils/downloadHelpers'
import { sortPolicyDeclarationFilesNewestFirst, formatAccessConfirmationLabelsFromFilename } from '@/lib/utils/formatters'
import type { PolicyDeclarationFile } from '../types'
import type { PolicyDeclarationFilesPopupProps } from '../types'

function getPolicyDeclarationFileId(file: PolicyDeclarationFile): string {
  return file.path || file.stream_url || file.filename || ''
}

const viewBtnClass =
  'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#1597ff] bg-white px-3 text-sm font-semibold text-[#1597ff] transition hover:bg-[#eef7ff] disabled:pointer-events-none disabled:opacity-60'

const downloadBtnClass =
  'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#1597ff] bg-[#1597ff] px-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc] disabled:pointer-events-none disabled:opacity-60'

export function PolicyDeclarationFilesPopup({
  isOpen,
  onClose,
  files,
}: PolicyDeclarationFilesPopupProps) {
  const [processing, setProcessing] = useState<Record<string, 'view' | 'download'>>({})
  const sortedFiles = useMemo(
    () => sortPolicyDeclarationFilesNewestFirst(files),
    [files],
  )

  const setProcessingKind = (id: string, kind: 'view' | 'download' | null) => {
    setProcessing((prev) => {
      if (!kind) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit `id` from the map
        const { [id]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [id]: kind }
    })
  }

  const viewFile = async (file: PolicyDeclarationFile) => {
    const id = getPolicyDeclarationFileId(file)
    setProcessingKind(id, 'view')
    try {
      await openServeFileInNewTab(file.stream_url)
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessingKind(id, null)
    }
  }

  const downloadFile = async (file: PolicyDeclarationFile) => {
    const id = getPolicyDeclarationFileId(file)
    setProcessingKind(id, 'download')
    try {
      await triggerServeFileDownload(
        file.stream_url,
        file.filename || 'terms-and-conditions.pdf',
      )
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessingKind(id, null)
    }
  }

  const count = sortedFiles.length
  const subtitle =
    count === 1 ? '1 confirmation document' : `${count} confirmation documents`

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Access Confirmations"
      subtitle={subtitle}
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      headerVariant="iconTitle"
      titleIcon={<FileText className="h-5 w-5 text-[#1597ff]" aria-hidden />}
      overlayDataTestId="access-confirmations-popup-overlay"
      panelDataTestId="access-confirmations-popup-panel"
      titleDataTestId="access-confirmations-popup-title"
      closeButtonDataTestId="access-confirmations-popup-close-button"
      bodyClassName="!px-0 !py-0"
    >
      <div
        className="!mx-5 !mt-4 !mb-4 flex items-start gap-2 rounded-lg border border-[#b6d9f5] bg-[#e8f4fc] px-3 py-2.5 text-sm text-[#1e3a5f]"
        data-testid="access-confirmations-info-banner"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#1597ff]" aria-hidden />
        <span>View or download previously generated access confirmations.</span>
      </div>

      <ul className="m-0 list-none divide-y divide-slate-100 p-0" data-testid="access-confirmations-file-list">
        {sortedFiles.map((file, index) => {
          const fileId = getPolicyDeclarationFileId(file)
          const viewBusy = processing[fileId] === 'view'
          const downloadBusy = processing[fileId] === 'download'
          const { dateLabel, timeLabel } = formatAccessConfirmationLabelsFromFilename(
            file.filename,
          )
          const title = dateLabel
            ? `Access Confirmation · ${dateLabel}`
            : file.filename || 'Access Confirmation'

          return (
            <li
              key={file.path || fileId || String(index)}
              className="flex flex-col gap-3 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              data-testid={`access-confirmations-row-${index}`}
            >
              <div className="flex min-w-0 items-start gap-3">
                <FileKindBadge filename={file.filename || ''} />
                <div className="min-w-0">
                  <p className="m-0 truncate text-sm font-semibold text-slate-900">{title}</p>
                  {timeLabel ? (
                    <p className="m-0 mt-0.5 text-xs text-slate-500">
                      Generated at {timeLabel}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                <button
                  type="button"
                  className={viewBtnClass}
                  disabled={viewBusy || downloadBusy}
                  onClick={() => void viewFile(file)}
                  data-testid={`access-confirmations-row-${index}-view`}
                >
                  {viewBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                  View
                </button>
                <button
                  type="button"
                  className={downloadBtnClass}
                  disabled={viewBusy || downloadBusy}
                  onClick={() => void downloadFile(file)}
                  data-testid={`access-confirmations-row-${index}-download`}
                >
                  {downloadBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden />
                  )}
                  Download
                </button>
              </div>
            </li>
          )
        })}
        {sortedFiles.length === 0 ? (
          <li className="px-5 py-8 text-center text-sm text-slate-500">No files available.</li>
        ) : null}
      </ul>
    </OnScreenModal>
  )
}
